'use client';
// based on https://ui.shadcn.com/docs/components/combobox

import { ComponentProps, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '../../Command';
import { Popover, PopoverContent, PopoverTrigger } from '../../Popover';

import styles from './comboBox.module.scss';
import { MdCheck, MdUnfoldMore } from 'react-icons/md';
import LoadingSpinner from '../../LoadingSpinner';

export type ComboBoxOptionBase = {
  label: string;
  value: string;
};

type ComboBoxProps<T extends ComboBoxOptionBase> = ComponentProps<'div'> & {
  options: T[];
  value?: string;
  setValue: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noOptionsMessage?: string;
  isLoading?: boolean;
  loadingPlaceholder?: string;
};

export default function ComboBox<T extends ComboBoxOptionBase>({
  value,
  setValue,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  noOptionsMessage = 'No options found.',
  isLoading = false,
  loadingPlaceholder = 'Loading...'
}: ComboBoxProps<T>) {
  const [open, setOpen] = useState(false);

  const getTriggerContent = () => {
    if (isLoading) {
      return loadingPlaceholder;
    }

    if (value) {
      return options.find((option) => option.value === value)?.label;
    }

    return placeholder;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={styles.trigger}
          role="combobox"
          aria-controls="listbox"
          aria-expanded={open}
          disabled={isLoading}
        >
          {getTriggerContent()}
          {isLoading ? (
            <LoadingSpinner size="small" />
          ) : (
            <MdUnfoldMore size={16} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className={styles.popover}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{noOptionsMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    setValue(currentValue === value ? '' : currentValue);
                    setOpen(false);
                  }}
                >
                  {option.label}
                  <MdCheck
                    className={`${styles.checkIcon} ${value !== option.value ? styles['checkIcon--hidden'] : ''}`}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
